import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useGlobal } from "reactn";
import classNames from "classnames";
import { getRiotAssetsContext } from "@outplayed/riot-assets";
import { Icon } from "@ugitgud/legos/ui/icon/Icon";
import { TierListChampionInfo } from "@ugg/shared/api/data-parser/tier-lists/tier-list";
import { QueueTypeS } from "@ugg/shared/utils/queue-type-helpers";
import {
  getLuTier,
  getTierColor,
  getTierGradeColorHex,
  TIER_GRADE_STYLES,
  getTierListPickRateCutoff,
} from "@ugg/shared/utils/tier-helpers";
import { getRoleLabel, getRevApiRole, getRoleSVG, RoleS } from "@ugg/shared/utils/role-helpers";
import { getChampionBuildUrl } from "@ugg/shared/routes/app-routes";
import { buildQueryParams } from "@ugg/shared/utils/url-query-params";
import { useQueryString } from "@ugg/shared/hooks/use-query-string";
import { convertPatchDisplay } from "@ugg/shared/utils/patch-helpers";
import { SvgMiscDoubleArrowUp } from "@ugitgud/legos/ui/icon/SVG/MiscDoubleArrowUp";
import { SvgMiscDoubleArrowDown } from "@ugitgud/legos/ui/icon/SVG/MiscDoubleArrowDown";

function formatMatches(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return String(n);
}

const TIERS_TO_SHOW = ["S+", "S", "A"] as const;
type VisTier = (typeof TIERS_TO_SHOW)[number];

const TIER_RANK: Record<string, number> = { "S+": 0, S: 1, A: 2, B: 3, C: 4, D: 5 };

function getTierChanges(data: TierListChampionInfo[], previousData?: TierListChampionInfo[]): Map<string, "up" | "down"> {
  const map = new Map<string, "up" | "down">();
  if (!previousData || previousData.length === 0) return map;
  const prevTierMap = new Map<string, string>();
  for (const champ of previousData) {
    const tier = getLuTier(champ.tier.stdevs);
    if (tier) prevTierMap.set(`${champ.champion_id}|${champ.role}`, tier);
  }
  for (const champ of data) {
    const currentTier = getLuTier(champ.tier.stdevs);
    if (!currentTier) continue;
    const prevTier = prevTierMap.get(`${champ.champion_id}|${champ.role}`);
    if (!prevTier || prevTier === currentTier) continue;
    const diff = TIER_RANK[prevTier] - TIER_RANK[currentTier];
    if (diff > 0) map.set(`${champ.champion_id}|${champ.role}`, "up");
    else if (diff < 0) map.set(`${champ.champion_id}|${champ.role}`, "down");
  }
  return map;
}

interface TierListVisualizationProps {
  className?: string;
  data: TierListChampionInfo[];
  previousData?: TierListChampionInfo[];
  filters: {
    queueType?: Partial<QueueTypeS>;
    allChampions?: "true" | "false";
    role?: string;
    patch?: string;
  };
  isExpanded: boolean;
  onExpandedChange: (value: boolean) => void;
}

interface TierListChampionCardProps {
  champion: TierListChampionInfo;
  tierColor: string;
  tierChange?: "up" | "down";
}

function TierListChampionCard({ champion, tierColor, tierChange }: TierListChampionCardProps) {
  const [language] = useGlobal("language");
  const { getChampionName, getNormalizedChampionName, getChampionImgFromSprite } = getRiotAssetsContext();
  const params = useQueryString();

  const championId = Number(champion.champion_id);
  const role = champion.role;
  const winRate = champion.win_rate;
  const showRoleBadge = role && role !== RoleS.NONE;

  const { role: _role, ...filteredParams } = params;

  const pathname = getChampionBuildUrl(getNormalizedChampionName(championId), {
    language,
    ...(role && role !== RoleS.NONE && { role: getRevApiRole(role) }),
  });

  const isUp = tierChange === "up";

  return (
    <Link
      className="relative group flex flex-col items-center justify-center gap-1 rounded w-full h-[100px] xs:h-[112px] max-w-[84px] xs:max-h-[112px] overflow-hidden bg-purple-400 shadow-[inset_0_1px_0_0_#32326E]"
      to={{ pathname, search: buildQueryParams(filteredParams) }}
    >
      {tierChange && (
        <div
          className={classNames(
            "absolute top-1 left-1 flex items-center justify-center w-3 h-3 rounded-[2px]",
            isUp ? "bg-accent-green-300/25" : "bg-accent-red-500/25",
          )}
        >
          <Icon
            icon={isUp ? <SvgMiscDoubleArrowUp /> : <SvgMiscDoubleArrowDown />}
            className={classNames("w-2.5 h-2.5", isUp ? "text-accent-green-300" : "text-accent-red-500")}
          />
        </div>
      )}
      <div className="relative shrink-0">
        <div className="rounded-full w-12 h-12 p-0.5 bg-purple-200 border" style={{ borderColor: tierColor }}>
          <div className="relative rounded-full overflow-hidden w-full h-full [&>div]:absolute [&>div]:top-1/2 [&>div]:left-1/2 [&>div]:-translate-x-1/2 [&>div]:-translate-y-1/2 [&>div]:scale-110">
            {getChampionImgFromSprite(championId, { size: 48 })}
          </div>
        </div>
        {showRoleBadge && (
          <div className="absolute right-0 bottom-0 flex rounded-sm items-center justify-center bg-purple-200 w-3.5 h-3.5">
            <div className="w-[10px] h-[10px] [&>svg]:w-full [&>svg]:h-full">{getRoleSVG(role)}</div>
          </div>
        )}
      </div>

      <span className="hidden xs:block text-[12px] font-semibold text-white text-center leading-tight px-1 w-full truncate group-hover:underline">
        {getChampionName(championId)}
      </span>

      <div className="hidden xs:block w-[60px] h-px bg-purple-100" />

      <div className="flex flex-col items-center gap-0 w-full text-center px-1">
        <span className="text-[10px] font-bold leading-tight">
          <span className={getTierColor(winRate)}>{winRate.toFixed(1)}%</span>
          <span className="ml-1 text-lavender-50">WR</span>
        </span>
        <span className="text-[10px] text-accent-gray-100 leading-tight">{formatMatches(champion.matches)} games</span>
      </div>
    </Link>
  );
}

export function TierListVisualization({
  className,
  data,
  previousData,
  filters,
  isExpanded,
  onExpandedChange,
}: TierListVisualizationProps) {
  const tierChangeMap = getTierChanges(data, previousData);

  const grouped = useMemo(() => {
    const cutoff = getTierListPickRateCutoff(filters.queueType as string | undefined);

    const result = TIERS_TO_SHOW.reduce<Record<VisTier, TierListChampionInfo[]>>(
      (acc, tier) => {
        acc[tier] = [];
        return acc;
      },
      { "S+": [], S: [], A: [] },
    );

    for (const champion of data) {
      if (filters.allChampions !== "true" && champion.tier.pick_rate < cutoff) continue;
      const tier = getLuTier(champion.tier.stdevs) as VisTier;
      if (tier in result) {
        result[tier].push(champion);
      }
    }

    for (const tier of TIERS_TO_SHOW) {
      result[tier].sort((a, b) => {
        const aAbove = a.tier.pick_rate >= cutoff;
        const bAbove = b.tier.pick_rate >= cutoff;
        if (aAbove && !bAbove) return -1;
        if (!aAbove && bAbove) return 1;
        if (a.tier.stdevs > b.tier.stdevs) return -1;
        if (a.tier.stdevs < b.tier.stdevs) return 1;
        return 0;
      });
    }

    return result;
  }, [data, filters.queueType, filters.allChampions]);

  if (!TIERS_TO_SHOW.some((t) => grouped[t].length > 0)) return null;

  return (
    <div className={classNames("tier-list-visualization rounded p-[18px] bg-purple-200", className)}>
      <div className="flex items-center gap-2 border-l-[3px] border-accent-blue-400 pl-2">
        <span className="text-white font-bold text-[14px]">Tier List</span>
        <span className="text-lavender-300 text-[14px]">
          {`The best champions${filters.role && filters.role !== RoleS.NONE ? ` for ${getRoleLabel(filters.role)}` : ""}${
            filters.patch ? ` in patch ${convertPatchDisplay(filters.patch).replace("_", ".")}` : ""
          }`}
        </span>
      </div>

      <div className="relative">
        <div
          className={classNames("overflow-hidden transition-all duration-300 p-[10px] -m-[10px]", {
            "max-h-[240px]": !isExpanded,
          })}
        >
          <div className="grid grid-cols-3 gap-[18px] pt-5">
            {TIERS_TO_SHOW.map((tier) => {
              const champions = grouped[tier];
              if (champions.length === 0) return null;
              const color = getTierGradeColorHex(tier);

              return (
                <div
                  key={tier}
                  className={classNames(
                    "rounded p-[18px] min-h-[246px] xs:min-h-[270px] relative bg-purple-400 shadow-[0_2px_2px_0_rgba(0,0,0,0.4)] border-t",
                    TIER_GRADE_STYLES[tier].cardClass,
                  )}
                  style={{
                    backgroundImage: `radial-gradient(ellipse at top left, ${color}1A 0%, ${color}0A 40%, transparent 100%)`,
                  }}
                >
                  <div
                    className={classNames(
                      "absolute -top-[13px] left-1/2 -translate-x-1/2 px-2.5 py-1 rounded text-[12px] font-bold whitespace-nowrap leading-tight border",
                      TIER_GRADE_STYLES[tier].badgeClass,
                    )}
                  >
                    {tier}
                  </div>

                  <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 gap-2 md:gap-4 justify-items-center">
                    {champions.slice(0, 12).map((champion, i) => (
                      <TierListChampionCard
                        key={`${champion.champion_id}-${i}`}
                        champion={champion}
                        tierColor={color}
                        tierChange={tierChangeMap.get(`${champion.champion_id}|${champion.role}`)}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {!isExpanded && (
          <div className="absolute bottom-0 left-0 right-0 h-[160px] pointer-events-none bg-gradient-to-t from-purple-200 via-purple-200/80 to-transparent" />
        )}

        <div
          className={classNames("flex justify-center", isExpanded ? "mt-[18px]" : "absolute bottom-0 left-0 right-0 pb-[12px]")}
        >
          <button
            className="flex items-center justify-center gap-1 rounded bg-accent-blue-400 hover:bg-accent-blue-600 text-white text-[13px] font-bold transition-colors cursor-pointer p-0 border-0 w-[180px] h-9"
            onClick={() => onExpandedChange(!isExpanded)}
          >
            <Icon
              icon={isExpanded ? <SvgMiscDoubleArrowUp /> : <SvgMiscDoubleArrowDown />}
              className="w-4 h-4 relative top-[-1px]"
            />
            <span className="leading-none">{isExpanded ? "Shrink" : "Click to Expand"}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
